import { noButtons } from '@byecode/ui'
import { Toast } from '@byecode/ui/components/Toast'
import type { ApplicationSettingAuthentication, ApplicationSettingNavbar, DataSourceAbstract, DevicePreviewEnum } from '@lighthouse/core'
import { isMiniprogram, useUncontrolled } from '@lighthouse/tools'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { emailRegex, mobileRegex } from '../../constants'
import { useApplicationContext } from '../../contexts/ApplicationContext'
import type { RoleOption, ScanStatus } from '../../types'
import { AppNavbarStylesContextProvider } from '../ApplicationContainer/AppNavbarStylesContext'
import type { CascadeParam, RelativeFieldParam } from '../FieldInput'
import { BindEmail } from './BindEmail'
import { BindMobile } from './BindMobile'
import { defaultConfig } from './constant'
import { Home } from './Home'
import { Login } from './Login'
import { Perfect } from './Perfect'
import type { AccountBindAccountForm, AccountLoginForm, AccountPerfectForm, AccountRegisterForm, AppLoginMode } from './types'
import { WechatLogin } from './WeChatLogin'

interface AccountPageProps extends RelativeFieldParam, CascadeParam {
    mode?: AppLoginMode
    defaultMode?: AppLoginMode
    authentication?: ApplicationSettingAuthentication
    previewType?: DevicePreviewEnum
    style?: React.CSSProperties
    roles?: RoleOption[]
    dataSource?: DataSourceAbstract
    disableEvent?: boolean
    navbar: ApplicationSettingNavbar
    language: string
    isWxOfficialAccount?: boolean
    onChangeLanguage: (v: string) => void
    onChangeMode?: (mode: AppLoginMode) => void
    onRegister?: (params: AccountRegisterForm) => void
    onLogin?: (params: AccountLoginForm) => void
    onPerfect?: (params: AccountPerfectForm) => void
    onHomeBack?: () => void
    onCheckWechatLogin?: (mark: string) => Promise<ScanStatus>
    onWeChatLogin?: () => Promise<{ url: string; mark: string }> | undefined
    onBindAccount?: (params: AccountBindAccountForm) => Promise<boolean>
    onSendCode?: (type: 'email' | 'mobile', account: string) => Promise<boolean>
}

const SCxAccountContainer = styled.div`
    flex: 1;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`
const SCxWrapper = styled.div`
    width: 100%;
    max-width: 390px;

    position: relative;
    margin: 60px 0;
    border-radius: 32px;
    border: 1px solid var(--color-gray-200);
    background: #fff;
    box-shadow: 0px 4px 12px 0px rgba(132, 146, 167, 0.12);

    max-height: 80%;
    overflow: hidden auto;
    ${noButtons('0px')}
`
export const AccountPage: React.FunctionComponent<AccountPageProps> = ({
    authentication = defaultConfig,
    defaultMode,
    dataSource,
    previewType,
    mode: propMode,
    roles,
    disableEvent,
    language,
    navbar,
    style,
    isWxOfficialAccount,
    dataSourceList,
    onChangeLanguage,
    onSendCode,
    onLogin,
    onRegister,
    onChangeMode,
    onPerfect,
    onWeChatLogin,
    onBindAccount,
    onHomeBack,
    onCheckWechatLogin,
    onFetchCascadeOptions,
    onFetchDataSource,
    onLoadMoreData
}) => {
    const { phone, weChat, google, email } = authentication.login

    const { isBuilder } = useApplicationContext()

    const [mode, setMode] = useUncontrolled({ value: propMode, onChange: onChangeMode, defaultValue: defaultMode })
    const [agreedToTerms, setAgreedToTerms] = React.useState(false)

    const { t } = useTranslation()

    const isShowWeChatLogin = useMemo(() => {
        if (!weChat.isOpened) {
            return false
        }

        // 没有开启h5以及小程序登录
        if (!weChat.isOpenedH5 && !weChat.isOpenedMiniProgram) {
            return false
        }

        // 管理端显示配置结果
        if (isBuilder) {
            return true
        }

        // 在小程序中
        if (isMiniprogram()) {
            return !!weChat.isOpenedMiniProgram
        }

        return weChat.isOpenedH5
    }, [isBuilder, weChat.isOpened, weChat.isOpenedH5, weChat.isOpenedMiniProgram])

    // 计算开启的登录方式数量
    const enabledLoginMethodsCount = React.useMemo(() => {
        let count = 0
        if (phone.isOpened) {
            count++
        }
        if (email.isOpened) {
            count++
        }
        if (isShowWeChatLogin) {
            count++
        }
        if (google.isOpened) {
            count++
        }
        return count
    }, [email.isOpened, google.isOpened, isShowWeChatLogin, phone.isOpened])

    const handleSendCode = useCallback(
        (type: 'email' | 'mobile', value: string) => {
            const isValid = type === 'email' ? emailRegex.test(value) : mobileRegex.test(value)
            // return Promise.resolve(true)
            if (!isValid) {
                Toast.error(type === 'email' ? t('emailFormatIsIllegal') : t('mobilePhoneNumberFormatIsIllegal'))
                return Promise.resolve(false)
            }
            return onSendCode?.(type, value)
        },
        [t, onSendCode]
    )

    const ele = useMemo(() => {
        switch (mode) {
            case 'home': {
                return (
                    <Home
                        authentication={authentication}
                        language={language}
                        isShowWeChatLogin={isShowWeChatLogin}
                        disableEvent={disableEvent}
                        previewType={previewType}
                        isWxOfficialAccount={isWxOfficialAccount}
                        agreedToTerms={agreedToTerms}
                        enabledLoginMethodsCount={enabledLoginMethodsCount}
                        onChangeLanguage={onChangeLanguage}
                        onWeChatLogin={onWeChatLogin}
                        onChangeMode={setMode}
                        onHomeBack={onHomeBack}
                        onAgreedToTermsChange={setAgreedToTerms}
                    />
                )
            }
            case 'bindWechat':
            case 'weChatLogin': {
                return (
                    <WechatLogin
                        language={language}
                        authentication={authentication}
                        onChangeMode={setMode}
                        onWeChatLogin={onWeChatLogin}
                        onChangeLanguage={onChangeLanguage}
                        onCheckWechatLogin={onCheckWechatLogin}
                    />
                )
            }
            // case 'register': {
            //     return (
            //         <Register
            //             authentication={authentication}
            //             disableEvent={disableEvent}
            //             language={language}
            //             previewType={previewType}
            //             onChangeLanguage={onChangeLanguage}
            //             onChangeMode={setMode}
            //             onSendCode={handleSendCode}
            //             onRegister={onRegister}
            //         />
            //     )
            // }
            case 'emailLogin': {
                return (
                    <Login
                        key="email"
                        type="email"
                        previewType={previewType}
                        language={language}
                        authentication={authentication}
                        disableEvent={disableEvent}
                        agreedToTerms={agreedToTerms}
                        enabledLoginMethodsCount={enabledLoginMethodsCount}
                        onChangeLanguage={onChangeLanguage}
                        onHomeBack={onHomeBack}
                        onChangeMode={setMode}
                        onSendCode={handleSendCode}
                        onLogin={onLogin}
                        onAgreedToTermsChange={setAgreedToTerms}
                    />
                )
            }
            case 'mobileLogin': {
                return (
                    <Login
                        key="mobile"
                        type="mobile"
                        authentication={authentication}
                        disableEvent={disableEvent}
                        language={language}
                        previewType={previewType}
                        agreedToTerms={agreedToTerms}
                        enabledLoginMethodsCount={enabledLoginMethodsCount}
                        onHomeBack={onHomeBack}
                        onChangeLanguage={onChangeLanguage}
                        onChangeMode={setMode}
                        onSendCode={handleSendCode}
                        onLogin={onLogin}
                        onAgreedToTermsChange={setAgreedToTerms}
                    />
                )
            }
            case 'bindEmail': {
                return (
                    <BindEmail
                        authentication={authentication}
                        language={language}
                        previewType={previewType}
                        onChangeLanguage={onChangeLanguage}
                        onChangeMode={setMode}
                        onSendCode={handleSendCode}
                        onBindAccount={onBindAccount}
                    />
                )
            }
            case 'bindMobile': {
                return (
                    <BindMobile
                        authentication={authentication}
                        language={language}
                        previewType={previewType}
                        onChangeLanguage={onChangeLanguage}
                        onChangeMode={setMode}
                        onSendCode={handleSendCode}
                        onBindAccount={onBindAccount}
                    />
                )
            }

            case 'perfect': {
                return (
                    <Perfect
                        authentication={authentication}
                        dataSource={dataSource}
                        roles={roles}
                        dataSourceList={dataSourceList}
                        onPerfect={onPerfect}
                        onFetchCascadeOptions={onFetchCascadeOptions}
                        onFetchDataSource={onFetchDataSource}
                        onLoadMoreData={onLoadMoreData}
                    />
                )
            }
            default: {
                return null
            }
        }
    }, [
        mode,
        authentication,
        language,
        isShowWeChatLogin,
        disableEvent,
        previewType,
        isWxOfficialAccount,
        agreedToTerms,
        enabledLoginMethodsCount,
        onChangeLanguage,
        onWeChatLogin,
        setMode,
        onHomeBack,
        onCheckWechatLogin,
        handleSendCode,
        onLogin,
        onBindAccount,
        dataSource,
        roles,
        dataSourceList,
        onPerfect,
        onFetchCascadeOptions,
        onFetchDataSource,
        onLoadMoreData
    ])

    return (
        <AppNavbarStylesContextProvider navbar={navbar}>
            <SCxAccountContainer style={style}>
                <SCxWrapper>{ele}</SCxWrapper>
            </SCxAccountContainer>
        </AppNavbarStylesContextProvider>
    )
}
